// @ts-check
// ════════════════════════════════════════════════════════════════
// FloorCost — localStorage wrapper
// ────────────────────────────────────────────────────────────────
// A thin wrapper around localStorage.setItem that catches quota
// errors and surfaces them to the user instead of silently
// discarding the write. All other errors re-throw so unexpected
// failures are not swallowed.
//
// Usage — drop-in replacement:
//   localStorage.setItem(key, JSON.stringify(val))
//   →  lsSet(key, JSON.stringify(val))
//
// `lsGetArray` is the read twin (TYPE-2). Same drop-in shape:
//   try { JSON.parse(localStorage.getItem(k)) || []; } catch { []; }
//   →  lsGetArray(k)
// ════════════════════════════════════════════════════════════════

import { parseStoredArray } from '#lib/parse-stored.js';
import { showToast } from '../../toast.js';

/**
 * Write a value to localStorage. On quota exceeded, shows a toast
 * prompting the user to export their data. On any other error,
 * re-throws so the caller's existing error handling still applies.
 *
 * Returns whether the write actually landed: `true` on success, `false`
 * on a quota failure. Callers that treat a write as durable — notably
 * the sync queue, where a dropped write is a silently lost mutation —
 * must check this rather than assume success.
 *
 * @param {string} key
 * @param {string} value  — already serialised (call JSON.stringify before passing)
 * @returns {boolean} true if the value was persisted
 */
export function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    // QuotaExceededError (Chrome/Safari) and NS_ERROR_DOM_QUOTA_REACHED (Firefox)
    // are both DOMExceptions. Surface them — the user must act.
    if (err instanceof DOMException) {
      showToast(
        'Storage full — save failed. Export your data to free up space.',
        'red',
      );
      console.error('[lsSet] quota exceeded writing key:', key);
      return false; // non-fatal — app keeps running, but the write was lost
    }
    throw err;
  }
}

/**
 * @typedef {{ key: string, value: string }} StorageWrite
 * @typedef {{
 *   ok: boolean,
 *   rolledBack: boolean,
 *   failedKey: string|null,
 *   rollbackFailedKeys: string[],
 *   error: unknown
 * }} StorageBatchResult
 */

/**
 * Persist a related set of already-serialized values with rollback on failure.
 * Snapshot every original raw value before writing anything. A failed result
 * with rolledBack=true means the original bytes were restored (or no write
 * was attempted). Rollback failure is reported explicitly, so callers can
 * reconcile live state with the remaining persisted values.
 *
 * This is synchronous best-effort rollback, not isolation from other windows.
 * Namespaced callers pass their current STORAGE key strings unchanged.
 *
 * @param {ReadonlyArray<StorageWrite>} writes
 * @returns {StorageBatchResult}
 */
export function lsSetBatch(writes) {
  /** @type {Map<string, string|null>} */
  const before = new Map();
  /** @type {string[]} */
  const attempted = [];
  /** @type {string|null} */
  let failedKey = null;
  try {
    for (const { key } of writes) {
      failedKey = key;
      if (!before.has(key)) before.set(key, localStorage.getItem(key));
    }
    for (const { key, value } of writes) {
      failedKey = key;
      attempted.push(key);
      if (!lsSet(key, value)) throw new Error('Could not persist storage key: ' + key);
    }
    return { ok: true, rolledBack: false, failedKey: null, rollbackFailedKeys: [], error: null };
  } catch (error) {
    /** @type {string[]} */
    const rollbackFailedKeys = [];
    // Repeated keys still roll back to their ORIGINAL value, once, in the
    // order of the last attempt. Rollback bypasses lsSet's quota toast.
    for (const key of new Set(attempted.reverse())) {
      const original = before.get(key) ?? null;
      try {
        if (original === null) localStorage.removeItem(key);
        else localStorage.setItem(key, original);
        if (localStorage.getItem(key) !== original) {
          throw new Error('Original storage value was not restored');
        }
      } catch (rollbackError) {
        rollbackFailedKeys.push(key);
        console.error('[lsSetBatch] rollback failed for key:', key, rollbackError);
      }
    }
    return {
      ok: false, rolledBack: rollbackFailedKeys.length === 0,
      failedKey, rollbackFailedKeys, error,
    };
  }
}

/**
 * Read a JSON array from localStorage, guaranteeing the caller gets an array.
 *
 * The counterpart to `lsSet`. Where `lsSet` refuses to let a failed write
 * pass as a success, this refuses to let a corrupt value pass as data: a
 * present-but-wrong value (bad JSON, or valid JSON of the wrong type) is
 * discarded in favour of an empty array, and reported to the console with
 * the key that holds it.
 *
 * Deliberately no toast. A failed write needs the operator to act — export
 * before more is lost — but by the time a corrupt value is read the data is
 * already gone, and these loaders run during render, where a toast would
 * re-fire on every repaint. The console line names the key so the state is
 * still diagnosable.
 *
 * @param {string} key
 * @returns {unknown[]} the stored array, or a fresh empty one
 */
export function lsGetArray(key) {
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch (err) {
    // Reads throw too, not just writes: Safari's private mode and a
    // blocked-cookies policy both make getItem raise SecurityError.
    console.error('[lsGetArray] could not read key:', key, err);
    return [];
  }

  const { value, status } = parseStoredArray(raw);
  if (status === 'unparseable' || status === 'wrong-shape') {
    console.error(`[lsGetArray] discarded ${status} value at key:`, key);
  }
  return value;
}

